import {
  ErrorResponse,
  type SigninRedirectArgs,
  type User,
} from "oidc-client-ts";
import { z } from "zod/v4";

type SessionRenewer = {
  signinSilent: () => Promise<User | null>;
};

type LoginRedirecter = {
  user?: User | null;
  signinRedirect: (args?: SigninRedirectArgs) => Promise<void>;
};

const TOKEN_EXPIRY_MARGIN_SECONDS = 60;

let pendingRenewal: Promise<User | null> | null = null;

export const renewSession = (renewer: SessionRenewer): Promise<User | null> => {
  if (!pendingRenewal) {
    pendingRenewal = renewer.signinSilent().finally(() => {
      pendingRenewal = null;
    });
  }
  return pendingRenewal;
};

export const isDefinitiveRenewalFailure = (error: unknown): boolean =>
  error instanceof ErrorResponse; // this excludes network blips or timeouts

const cognitoIdentitiesClaimParser = z
  .array(z.object({ providerName: z.string().nonempty() }))
  .nonempty();

const federatedIdentityProviderOf = (
  user: User | null | undefined,
): string | undefined =>
  cognitoIdentitiesClaimParser.safeParse(user?.profile.identities).data?.[0]
    .providerName;

let pendingLoginRedirect: Promise<void> | null = null;

export const redirectToLogin = (auth: LoginRedirecter): Promise<void> => {
  if (!pendingLoginRedirect) {
    const identityProvider = federatedIdentityProviderOf(auth.user);
    pendingLoginRedirect = auth
      .signinRedirect(
        identityProvider
          ? { extraQueryParams: { identity_provider: identityProvider } }
          : undefined,
      )
      .catch(() => {})
      .finally(() => {
        pendingLoginRedirect = null;
      });
  }
  return pendingLoginRedirect;
};

const SESSION_END_GRACE_MS = 1_000;

let sessionEndRedirect: ReturnType<typeof setTimeout> | null = null;

// A plain redirect before the session has actually ended lets the IdP
// silently re-issue another dying session, so the redirect waits the token out.
export const redirectToLoginWhenSessionEnds = (
  auth: LoginRedirecter,
  idTokenExpiresAtSeconds: number,
): void => {
  if (sessionEndRedirect) {
    clearTimeout(sessionEndRedirect);
  }
  sessionEndRedirect = setTimeout(
    () => {
      sessionEndRedirect = null;
      void redirectToLogin(auth);
    },
    Math.max(
      idTokenExpiresAtSeconds * 1_000 + SESSION_END_GRACE_MS - Date.now(),
      0,
    ),
  );
};

export const isTokenExpired = (user: User): boolean => {
  const expiresAt = user.profile.exp;
  if (typeof expiresAt === "number") {
    return expiresAt <= Date.now() / 1000 + TOKEN_EXPIRY_MARGIN_SECONDS;
  }
  return user.expired ?? false;
};
